<template>
	<v-dialog v-model="dialog" max-width="950px">
		<v-card class="px-5 pt-5">
			<p class="text-h6">{{ $t('duty_root_dialog_title') }}</p>

			<v-expansion-panels v-if="changedDutyProperties.length">
				<v-expansion-panel elevation="1">
					<v-expansion-panel-title>
						<div>
							<span>{{ selectedDutyTitle }}</span>
							<span> · </span>
							<span class="text-grey">
								{{ formatDate(daysRef?.[selectedDuty?.day]) }}
							</span>
						</div>
					</v-expansion-panel-title>

					<v-expansion-panel-text>
						<div class="pt-3">
							{{ selectedDutyChangedProperty }}
						</div>

						<div v-for="(change, index) in changedPropertyHistory" :key="index" class="pt-3">
							<span>{{ change.key }}: </span>
							<span class="text-grey">{{ change.oldValue }}</span>

							<v-icon size="small">mdi-arrow-right</v-icon>

							<span class="font-weight-bold">
								{{ change.newValue }}
							</span>
						</div>
					</v-expansion-panel-text>
				</v-expansion-panel>
			</v-expansion-panels>

			<div v-else class="d-flex align-center mt-3">
				<v-icon class="mr-2" color="blue"> mdi-information-outline </v-icon>
				<p class="mb-0">{{ $t('no_duty_properties_updated') }}</p>
			</div>

			<v-expansion-panels v-model="activePanel">
				<v-expansion-panel class="mt-5">
					<v-expansion-panel-title>
						{{ $t('duty_root_dialog_other_duties_title') }}
					</v-expansion-panel-title>

					<v-expansion-panel-text>
						<v-row align="center">
							<v-col cols="3">
								<v-checkbox color="primary-green" v-model="selectAll" :label="$t('action_select_all')" />
							</v-col>
							<v-col cols="2">
								<v-btn variant="text" class="mb-2 mr-6 text-body-2" @click="resetFilter">{{ $t('performance_metrics.btn_clear') }}</v-btn>
							</v-col>

							<v-col cols="12" sm="6">
								<v-autocomplete
									v-model="selectedDutyStatus"
									:items="dayStatuses"
									item-title="text"
									item-value="value"
									multiple
									variant="outlined"
									density="compact"
									chips
									:label="$t('select_status')"
									item-color="primary"
									hide-details
								/>
							</v-col>
						</v-row>

						<v-divider />

						<v-list class="scrollable-list py-2">
							<v-list-item v-for="item in formattedFilterDuties" :key="item._id" density="compact" @click="toggleItem(item)" class="py-3">
								<template #prepend>
									<v-checkbox
										:model-value="selectedDutiesSet.has(item._id)"
										@update:modelValue="() => toggleItem(item)"
										density="compact"
										color="primary-green"
										hide-details
									/>
								</template>

								<v-list-item-title class="text-body-1 ml-3">
									{{ item.nameKey ? t(item.nameKey) : item.name }}
									<span class="text-grey ml-2"> ({{ item.formattedDate }}) </span>
									<v-chip class="ml-1" color="grey" label>
										<span class="text-black">{{ $t(`planning_duty_status_${item?.day?.status}`) }}</span>
									</v-chip>
								</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-expansion-panel-text>
				</v-expansion-panel>
			</v-expansion-panels>

			<v-card-actions
				><v-btn v-if="edit" color="#000000DE" variant="text" @click="close">
					{{ $t('action_cancel') }}
				</v-btn>
				<v-spacer />
				<v-btn v-if="edit && (!isPastOrPublishedDay || !isActive)" variant="flat" color="#F5F5F5" @click="submit">
					{{ $t('action_save') }}
				</v-btn>
				<v-btn
					v-if="edit && isPastOrPublishedDay && !!usePreviewStore().currentPreview"
					variant="flat"
					color="teal-darken-4"
					:loading="previewButtonLoading"
					@click="() => handlePreviewButtonClick(() => submit())"
				>
					<v-progress-circular v-if="previewButtonLoading" indeterminate size="18" width="2" />
					<span v-else>{{ $t(previewButtonLabelKey) }}</span>
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useUserActionsBridgeStore } from '@/rennovation/planning-view/store/userActionsBridge';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { DateTime, Duration } from 'luxon';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { useDateFormatter } from '@/rennovation/common/composables/use-date-formatter';
import { useDateTime } from '../../../../../../components/composables/useDateTime';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import { usePreviewStore } from '@/rennovation/planning-view/store/usePreviewStore';

const emit = defineEmits(['update:modelValue', 'submit', 'cancel']);

const props = defineProps<{
	modelValue: boolean;
	selectedDuty: any;
	changedDuty: any;
	rootDuties: any[];
	day?: string;
}>();

const { t } = useI18n();
const { formatSecondsDuration } = useDateTime();
const qualificationStore = useUserActionsBridgeStore();
const store = useStore();
const dayStore = useDayStore();

onMounted(() => {
	if (!qualificationStore.qualifications?.length) {
		qualificationStore.fetchQualifications();
	}
});

const dialog = computed({
	get: () => props.modelValue,
	set: (value: boolean) => emit('update:modelValue', value),
});

const changedDutyProperties = computed(() => {
	if (!props.changedDuty) return [];
	return Array.isArray(props.changedDuty) ? props.changedDuty : Object.entries(props.changedDuty).map(([key, value]) => ({ [key]: value }));
});

const daysRef = computed(() => useDayStore().daysRef);
const qualifications = computed(() => qualificationStore.qualifications);
const locale = computed(() => store.state.profile.locale);
const edit = computed(() => store.getters['auth/checkPermission']('duties.edit'));
const { formatDateByLocale } = useDateFormatter();
const { isActive, previewButtonLoading, previewButtonLabelKey, handlePreviewButtonClick } = useDutyChangePreview();
const isPastOrPublishedDay = computed(() => {
	const selectedDate = daysRef.value?.[props.day];
	return dayStore.isPastOrPublishedDay(selectedDate);
});
const extraPayAccounts = [
	{ name: 'Dienst an freien Tag', abbreviation: 'DafT', number: 1234 },
	{ name: 'LS-Pikett', abbreviation: 'LS-P', number: 1556 },
	{ name: 'Betriebspikett', abbreviation: 'BP', number: 1558 },
	{ name: 'LS-Zulage Sa', abbreviation: 'LS Sa', number: 1562 },
	{ name: 'LS-Zulage So', abbreviation: 'LS So', number: 1572 },
	{ name: 'Lange Pause (>3Std)', abbreviation: 'LaPa', number: 1580 },
];

const accountMap = new Map(extraPayAccounts.map((acc) => [acc.number, acc.name]));

// Map optimization for qualifications lookup
const qualificationsMap = computed(() => {
	const map = new Map();
	const list = qualifications.value || [];
	for (let i = 0; i < list.length; i++) {
		const q = list[i];
		if (q?.id != null) map.set(q.id, q);
		if (q?._id != null) map.set(q._id, q);
	}
	return map;
});

const activePanel = ref(0);
const selectedDuties = ref<any[]>([]);
const selectAllManuallySet = ref(false);

const ALL_STATUSES = ['open', 'planning', 'preview', 'published', 'past'];
const PAST_PUBLISHED_STATUSES = ['past', 'published'];
const OTHER_STATUSES = ['open', 'planning', 'preview'];

const allowedStatuses = computed(() => {
	if (!isActive.value) return ALL_STATUSES;
	return isPastOrPublishedDay.value ? PAST_PUBLISHED_STATUSES : OTHER_STATUSES;
});

const defaultStatuses = computed(() => {
	if (!isActive.value) return ['published'];
	return isPastOrPublishedDay.value ? ['published'] : ['planning'];
});

const storageKey = computed(() => {
	if (!isActive.value) return 'selectedDutyStatus';
	return `selectedDutyStatus_${isPastOrPublishedDay.value ? 'pastPublished' : 'other'}`;
});

const loadSelectedStatuses = (): string[] => {
	try {
		const saved = JSON.parse(localStorage.getItem(storageKey.value) || 'null');
		const valid = Array.isArray(saved) ? saved.filter((s: string) => allowedStatuses.value.includes(s)) : [];
		return valid.length ? valid : [...defaultStatuses.value];
	} catch {
		return [...defaultStatuses.value];
	}
};

const selectedDutyStatus = ref<string[]>(loadSelectedStatuses());

const dayStatuses = computed(() =>
	allowedStatuses.value.map((status) => ({
		text: t(`planning_duty_status_${status}`),
		value: status,
	}))
);

const selectedDutiesSet = computed(() => new Set(selectedDuties.value.map((d) => d._id)));
const selectedDutyTitle = computed(() => t('duty_root_dialog_original_title', { dutyName: props.selectedDuty?.name }));
const selectedDutyChangedProperty = computed(() => {
	const keys = changedDutyProperties.value.map((obj: any) => Object.keys(obj)[0]);
	if (!keys.length) return '';

	let result = '';
	if (keys.length === 1) {
		result = t(`duty_changed_property_${keys[0]}`);
	} else if (keys.length === 2) {
		result = `${t(`duty_changed_property_${keys[0]}`)} ${t('and')} ${t(`duty_changed_property_${keys[1]}`)}`;
	} else {
		const allButLast = keys
			.slice(0, -1)
			.map((key: string) => t(`duty_changed_property_${key}`))
			.join(', ');
		result = `${allButLast}, ${t('and')} ${t(`duty_changed_property_${keys[keys.length - 1]}`)}`;
	}
	return t('duty_root_updated_properties', { properties: result });
});

const formatExtraPay = (accountObj: Record<string | number, number> | undefined) => {
	if (!accountObj || Object.keys(accountObj).length === 0) return '';

	return Object.entries(accountObj)
		.map(([numStr, qty]) => {
			const name = accountMap.get(Number(numStr)) || numStr; // Fallback to key if not found
			return `${name}: ${qty}`;
		})
		.join(', ');
};

const changedPropertyHistory = computed(() => {
	const qMap = qualificationsMap.value;
	const ids = Object.values(props.selectedDuty?.qualifications || []).map((item: any) => item._id);
	const isValid = (val: any) => val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);

	return changedDutyProperties.value.map((item: any) => {
		const key = Object.keys(item)[0];
		const value = Object.values(item)[0];
		let oldValue: any = '';
		let newValue: any = '';

		switch (key) {
			case 'duty':
				oldValue = props.selectedDuty?.['name'];
				newValue = value ? value : t('duty_dayType_unassigned');
				break;
			case 'qualifications':
				oldValue = ids
					.map((id: string) => qMap.get(id)?.name)
					.filter(Boolean)
					.join(', ');
				newValue = value
					.map((id: string) => qMap.get(id)?.name)
					.filter(Boolean)
					.join(', ');

				break;
			case 'type':
				oldValue = t(`duty_dayType_${props.selectedDuty?.['type']}`);
				newValue = t(`duty_dayType_${value}`);
				break;
			case 'fixed':
				oldValue = props.selectedDuty?.['fixed'];
				newValue = value;
				break;
			case 'breaks':
				oldValue = formattedBreaks(props.selectedDuty?.['breaks']);
				newValue = formattedBreaks(value);
				break;
			case 'start':
			case 'end':
			case 'standbyStart':
			case 'standbyEnd':
			case 'workTime':
			case 'additionalWorkTime':
				oldValue = formatSecondsDuration(props.selectedDuty?.[key]);
				newValue = formatSecondsDuration(value as number);
				break;
			case 'extraPayAccountQuantity':
				oldValue = formatExtraPay(props.selectedDuty?.[key]);
				newValue = formatExtraPay(value);
				break;
			case 'labels':
				oldValue =
					props.selectedDuty?.[key]
						?.map((i: any) => i.name)
						.filter(Boolean)
						.join(', ') || '';
				newValue =
					value
						?.map((i: any) => i.name)
						.filter(Boolean)
						.join(', ') || '';
				break;
			default:
				oldValue = props.selectedDuty?.[key];
				newValue = value;
				break;
		}

		return {
			key: t(`duty_changed_property_${key}`),
			oldValue: isValid(oldValue) ? oldValue : t('no_data'),
			newValue: isValid(newValue) ? newValue : t('no_data'),
		};
	});
});

const formattedFilterDuties = computed(() => {
	return props.rootDuties
		.filter((duty: any) => {
			if (!selectedDutyStatus.value?.length) {
				return false;
			}
			return selectedDutyStatus.value.includes(duty.day?.status);
		})
		.map((duty: any) => ({
			...duty,
			formattedDate: formatDate(duty.day?.date),
		}));
});

const selectAll = computed({
	get() {
		const filterLen = formattedFilterDuties.value.length;
		return filterLen > 0 && selectedDuties.value.length === filterLen;
	},
	set(value: boolean) {
		selectAllManuallySet.value = value;
		selectedDuties.value = value ? [...formattedFilterDuties.value] : [];
	},
});
const formatDate = (date: string) => {
	if (!date) return '';
	return formatDateByLocale(date);
};

const formatDateTime = (dateTime: number) => {
	if (!dateTime) return '';
	return Duration.fromMillis(dateTime * 1000).toFormat('hh:mm');
};

const secondsToHMS = (sec: number) => {
	const h = Math.floor(sec / 3600);
	const m = Math.floor((sec % 3600) / 60);
	return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

const formattedBreaks = (breaks: any[]) => {
	console.log(breaks, 'breaks');
	if (!breaks || breaks.length === 0) return [];
	return breaks
		.map((b) => {
			const startTime = b.start === 0 ? b.start : formatDateTime(b.start);
			const endTime = b.end === 0 ? b.end : formatDateTime(b.end);
			const durationFormatted = secondsToHMS(b.duration);
			const paidStatus = b.paid ? 'Paid' : 'Unpaid';
			return `• ${startTime} – ${endTime} (${paidStatus}, ${durationFormatted})`;
		})
		.join('\n');
};

const toggleItem = (item: any) => {
	const index = selectedDuties.value.findIndex((i) => i._id === item._id);
	if (index !== -1) {
		selectedDuties.value.splice(index, 1);
	} else {
		selectedDuties.value.push(item);
	}
};

const submit = () => emit('submit', selectedDuties.value);
const resetFilter = () => {
	selectedDutyStatus.value = [...defaultStatuses.value];
	selectedDuties.value = [];
	selectAllManuallySet.value = false;
};
const close = () => {
	selectedDuties.value = [];
	emit('update:modelValue', false);
	emit('cancel');
};

watch(
	() => props.rootDuties,
	(newVal) => {
		if (selectAllManuallySet.value) {
			const currentIds = selectedDutiesSet.value;
			const newItems = newVal.filter((item: any) => !currentIds.has(item._id));
			if (newItems.length > 0) {
				selectedDuties.value = [...selectedDuties.value, ...newItems];
			}
		}
	}
);

watch([isActive, isPastOrPublishedDay], () => {
	selectedDutyStatus.value = loadSelectedStatuses();
	selectedDuties.value = [];
	selectAllManuallySet.value = false;
});

watch(selectedDutyStatus, (value) => {
	localStorage.setItem(storageKey.value, JSON.stringify(value));
});
</script>

<style scoped>
.scrollable-list {
	max-height: 300px;
	overflow-y: auto;
}
.v-list-item {
	padding: 0;
}
</style>
